import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import moment from 'moment';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import {
    filter,
    map,
    share,
    take,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { ServiceItem } from '..';
import { environment } from '../../../environments/environment';
import * as fromDataServices from '../../data-services';
import { ServiceItemHistory } from '../../data-services/service-item/history';
import { PostingType } from '../../lookups';
import * as fromCommon from '../../shared';
import * as fromShared from '../../shared';
import { DialogService, Process, SelectItem, uniqBy } from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromUser from '../../user-info';
import * as fromStore from '../store';
import { SyncDialogContainerComponent } from '../sync-dialog/sync-dialog-container/sync-dialog-container.component';
@Component({
    selector: 'ef-service-item-container',
    templateUrl: './service-item-container.component.html',
    styleUrls: ['./service-item-container.component.scss'],
    standalone: false
})
export class ServiceItemContainerComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    process$: Observable<Process>;
    serviceItems$: Observable<ServiceItem[]>;
    serviceItemsSelectItems$: Observable<SelectItem[]>;
    sessionsSelectItems$: Observable<SelectItem[]>;
    serviceItemHistories$: Observable<ServiceItemHistory[]>;
    postingItems$: Observable<PostingType[]>;
    selectedError$: Observable<fromShared.Errors.IShowErrors>;
    selectedErrorWithStatus$: Observable<fromCommon.IStatusMessage>;
    userInfo$: Observable<fromUser.IUserInfo>;
    serviceItemHistoryDataService: EntityCollectionService<fromDataServices.ServiceItem.History.ServiceItemHistory>;
    active = true;
    historyDates: Date[] = [];
    SavePostingType = new ReplaySubject<PostingType>(1);
    ShowErrorSubject = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    SelectItemSubject = new ReplaySubject<string[]>(1);
    DatesChangedSubject = new ReplaySubject<Date[]>(1);
    SyncServiceitemsSubject = new ReplaySubject(1);
    dropdownItemHeight = environment.dropdownItemHeight;
    formAddErrors: {
        severity: string;
        detail: string;
    }[];
    syncFormGroup: UntypedFormGroup;
    moment: any;
    public tabHeight = '15vh';
    activeIndex: number[] = [0, 1];
    private dialogRef: fromCommon.DynamicDialogRef;
    // @ViewChild('divHistory', { static: true }) divHistory: ElementRef;
    constructor(
        private log: LoggingService,
        public entityServices: EntityServices,
        private renderer: Renderer2,
        private store: Store<fromStore.State>,
        private dialogService: DialogService
    ) {
        this.serviceItemHistoryDataService =
            entityServices.getEntityCollectionService<fromDataServices.ServiceItem.History.ServiceItemHistory>(
                fromDataServices.ServiceItem.History.entityName
            );
        this.syncFormGroup = new UntypedFormGroup({
            SIListID: new UntypedFormControl(null, Validators.required),
            FromSession: new UntypedFormControl(null, Validators.required),
            syncType: new UntypedFormControl()
            // PIListID: new FormControl(null, Validators.required)
        });
        this.log.verbose({
            type: 'ServiceItemContainerComponent::'
        });
        this.moment = moment;
    }

    ngOnInit() {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:ngOnInit'
        });

        this.store.dispatch(fromStore.getServiceItems());
        this.serviceItemHistoryDataService.clearCache();
        this.setupObservables();
        this.handleOnOpen([0, 1]);
    }
    ngAfterViewInit(): void {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:ngAfterViewInit'
        });

        setTimeout(() => {
            this.setupSubscriptions();
        }, 0);
    }
    ngOnDestroy() {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:ngOnDestroy'
        });
        this.active = false;
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }
    setupObservables() {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:setupObservables'
        });
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.userInfo$ = this.store.pipe(
            select(fromUser.getUserInfo),
            filter(u => !!u)
        );
        this.userInfo$
            .pipe(
                takeWhile(() => this.active),
                map(u => [u.FYStartDate, u.FYEndDate]),
                filter(([start, end]) => !!start && !!end)
            )
            .subscribe(([start, end]) => {
                this.historyDates[0] = start;
                this.historyDates[1] = end;
                this.onHistoryDatesChanged();
            });

        this.serviceItems$ = this.store.pipe(
            select(fromStore.selectAll),
            share(),
            tap(serviceItems =>
                this.log.verbose({
                    type: 'ServiceItemContainerComponent:setupObservables:serviceItems',
                    serviceItems
                })
            ),
            map(items =>
                clone(items).sort((a: ServiceItem, b: ServiceItem) =>
                    a.ServiceItem.toLocaleLowerCase().localeCompare(
                        b.ServiceItem.toLocaleLowerCase()
                    )
                )
            )
        );
        this.serviceItemsSelectItems$ = this.serviceItems$.pipe(
            map(items =>
                items
                    .map(i => ({ value: i.SIListID, label: i.ServiceItem }))
                    .sort((a, b) =>
                        a.label
                            .toLocaleLowerCase()
                            .localeCompare(b.label.toLocaleLowerCase())
                    )
            )
        );
        this.postingItems$ = this.store.pipe(
            select(fromStore.selectServiceItemPostingTypes),
            map(items => clone(items) as PostingType[]),
            withLatestFrom(this.serviceItems$),
            map(([items, serviceItems]) =>
                items.map(x => ({
                    ...x,
                    ServiceItemDescription:
                        serviceItems.findIndex(y => y.SIListID === x.ListID) >=
                        0
                            ? serviceItems.find(y => y.SIListID === x.ListID)
                                  .ServiceItemDescription
                            : ''
                }))
            )
        );
        this.serviceItemHistories$ =
            this.serviceItemHistoryDataService.entities$.pipe(
                map(items => clone(items))
            );
        this.selectedError$ = this.store.pipe(
            select(fromShared.Errors.getShowErrors),
            tap(selected =>
                this.log.verbose({
                    type: 'ServiceItemContainerComponent:selectedError$',
                    selected
                })
            )
        );
        const errors$ = this.store.pipe(select(fromShared.Errors.getErrors));
        this.selectedErrorWithStatus$ = combineLatest([
            this.selectedError$,
            errors$,
            this.serviceItems$.pipe(filter(x => !!x))
        ]).pipe(
            tap(([selected, errors]) =>
                this.log.verbose({
                    type: 'ServiceItemContainerComponent:errors$',
                    errors,
                    selected
                })
            ),
            map(([selected, errors]) =>
                errors.filter(
                    e =>
                        e.MsgEntityID === selected.MsgEntityID &&
                        e.MsgProcessID === selected.MsgProcessID
                )
            ),
            map(items => ({ ...items[0] })),
            tap(selected =>
                this.log.verbose({
                    type: 'ServiceItemContainerComponent:errors$:filtered',
                    selected
                })
            )
        );
    }
    setupSubscriptions() {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:setupSubscriptions'
        });
        this.SavePostingType.pipe(
            takeWhile(() => this.active),
            tap(pt =>
                this.log.verbose({
                    type: 'ServiceItemContainerComponent:SavePostingType',
                    pt
                })
            ),
            withLatestFrom(this.serviceItems$)
        ).subscribe(x => {
            // this.log.verbose({
            //     type: 'ServiceItemContainerComponent:SavePostingType',
            //     item,
            //     serviceItems
            // });
            // const si = serviceItems.find(x => x.SIListID === item.ListID);
            // this.store.dispatch(
            //     fromStore.putServiceItems({
            //         items: [{ ...si, Postingtype: item.PostingType }]
            //     })
            // );
        });

        combineLatest([this.process$, this.ShowErrorSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.log.verbose({
                    type: 'ServiceItemContainerComponent:this.process$, this.ShowErrorSubject'
                });
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });

        combineLatest([
            this.SelectItemSubject,
            this.DatesChangedSubject.pipe(
                filter(x => Array.isArray(x) && x.length === 2),
                filter(([start, end]) => !!start && !!end),
                tap(x => this.log.verbose({ type: 'DatesChangedSubject', x }))
            )
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([item, dates]) => {
                this.log.verbose({
                    type: 'ServiceItemContainerComponent:SelectItemSubject,DatesChangedSubject',
                    item,
                    dates
                });
                this.serviceItemHistoryDataService.clearCache();
                if (item) {
                    this.serviceItemHistoryDataService.getWithQuery({
                        SIListIDs: item.map(x => ({ SIListID: x })) as any,
                        StartDate: moment(dates[0]).format('MM/DD/YYYY'),
                        EndDate: moment(dates[1]).format('MM/DD/YYYY')
                    });
                }
            });

        this.SyncServiceitemsSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.DatesChangedSubject),
            withLatestFrom(
                this.serviceItemHistories$.pipe(
                    map(items =>
                        items.map(item => ({
                            label: `${moment(item.SessionStart).format(
                                'MM/DD/YYYY'
                            )} - ${moment(item.SessionEnd).format(
                                'MM/DD/YYYY'
                            )}`,
                            value: `${moment(item.SessionStart).format(
                                'MM/DD/YYYY'
                            )} - ${moment(item.SessionEnd).format(
                                'MM/DD/YYYY'
                            )}`
                        }))
                    ),
                    map(items => uniqBy(items, x => x.value))
                )
            )
        ).subscribe(([[syncType, dates], fromPeriods]) => {
            this.log.verbose({
                type: 'ServiceItemContainerComponent:syncFormGroup',
                syncType,
                dates
            });
            this.dialogRef = this.dialogService.open(
                SyncDialogContainerComponent,
                {
                    header: 'Sync with current rates', // default choice
                    closable: false,
                    closeOnEscape: false,
                    showHeader: true,
                    modal: true,
                    style: {
                        minWidth: '20dvw',
                        minHeight: '20dvh'
                    },
                    contentStyle: {
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    },
                    data: {
                        syncType,
                        dates,
                        fromPeriods
                    }
                }
            );
            this.dialogRef.onClose
                .pipe(withLatestFrom(this.SelectItemSubject), take(1))
                .subscribe(([result, selected]) => {
                    this.log.verbose({
                        type: 'ServiceItemContainerComponent:syncFormGroup:dialogRef:onClose',
                        result,
                        selected
                    });
                    if (result) {
                        this.onSaveServiceItemHistory(
                            {
                            ServiceItems: selected.map(si => ({
                                SIListID: si,
                                ServiceItem: '',
                                ServiceItemDesc: ''
                            })),
                            SessionStart: result.ToPeriod.split('-')[0].trim(),
                            SessionEnd: result.ToPeriod.split('-')[1].trim(),
                            FromStartDate:
                                result.FromPeriod && result.FromPeriod.length
                                    ? result.FromPeriod.split('-')[0].trim()
                                    : null,
                            FromEndDate:
                                result.FromPeriod && result.FromPeriod.length
                                    ? result.FromPeriod.split('-')[1].trim()
                                    : null,
                            SyncSI: true
                        } as any,
                            selected,
                            dates
                        );
                    }
                });
        });
    }
    onSave($event: ServiceItem) {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:onSave',
            $event
        });
        this.store.dispatch(fromStore.putServiceItems({ items: [$event] }));
    }
    onSavePostingType($event: PostingType) {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:onSavePostingType',
            $event
        });
        // this.SavePostingType.next($event);
    }
    onSaveServiceItemHistory(
        item: ServiceItemHistory,
        selectedSIListIDs?: string[],
        dates?: Date[]
    ) {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:onSaveServiceItemHistory',
            item
        });
        this.serviceItemHistoryDataService
            .upsert(item)
            .pipe(take(1))
            .subscribe(() => {
                if (
                    selectedSIListIDs &&
                    selectedSIListIDs.length > 0 &&
                    dates &&
                    dates.length === 2 &&
                    dates[0] &&
                    dates[1]
                ) {
                    this.serviceItemHistoryDataService.clearCache();
                    this.serviceItemHistoryDataService.getWithQuery({
                        SIListIDs: selectedSIListIDs.map(x => ({
                            SIListID: x
                        })) as any,
                        StartDate: moment(dates[0]).format('MM/DD/YYYY'),
                        EndDate: moment(dates[1]).format('MM/DD/YYYY')
                    });
                }
            });
    }
    onShowError($event) {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:onShowError',
            $event
        });
        this.ShowErrorSubject.next($event);
    }
    onHistoryDatesChanged() {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:onHistoryDatesChanged',
            historyDates: this.historyDates
        });
        this.DatesChangedSubject.next(this.historyDates);
    }
    handleOnOpen($event) {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:handleOnOpen',
            $event,
            activeIndex: this.activeIndex
        });
        const baseHeight = 68;

        this.tabHeight = `${
            baseHeight / ($event && $event.length ? $event.length : 1) - 1
        }vh`;
    }
    onSelect($event: ServiceItem[]) {
        this.log.verbose({
            type: 'ServiceItemContainerComponent:onSelect',
            $event
        });
        if ($event && Array.isArray($event)) {
            this.SelectItemSubject.next($event.map(x => x.SIListID));
        }
    }
}
